import { Component, Element, Event, EventEmitter, h, Listen, Prop, State } from '@stencil/core';
import { BielWebSocketService } from '../../services/biel-websocket-service';
import { BielService } from '../../services/biel-service';

@Component({
  tag: 'biel-bot',
  styleUrl: 'biel-bot.css',
  shadow: true
})
export class BielBot {
  private TERMS_ACCEPTED_KEY = 'biel_terms_accepted';

  // Define the component properties
  @Prop({ mutable: true }) modalPosition: string = 'center';
  @Prop() project: string = '';
  @Prop({ mutable: true, reflect: true }) showModal: boolean = false;
  @Prop() fetchData: boolean = true;
  @Prop() email: string = '';
  @Prop() headerTitle: string = 'Company Name AI';
  @Prop() inputPlaceholderText: string = 'Type your message';
  @Prop() sendButtonText: string = 'Send';
  @Prop({ mutable: true }) hideCloseButton: boolean = false;
  @Prop({ mutable: true }) hideExpandButton: boolean = false;
  @Prop({ mutable: true }) hideRefreshButton: boolean = false;
  @Prop() expandModal: boolean = false;
  @Prop() errorMessage404: string = "Oops! We could not find the provided project ID.";
  @Prop() errorMessage403: string = "Oops! The request URL does not match the one defined for this project.";
  @Prop() errorMessageDefault: string = "Oops! Please try again later.";
  @Prop() footerText: string = "AI-generated answers may contain errors. Verify official sources before use.";
  @Prop() suggestedQuestionsTitle: string = 'Suggested questions';
  @Prop() initialMessages: { text: string, sender: 'user' | 'ai', feedback?: 0 | 1, isPartial: false }[] = [];
  @Prop() disableInput: boolean = false;
  @Prop() hideFeedback: boolean = false;
  @Prop() embeddedMode: boolean = false;
  @Prop() bielId: string = `biel-bot-${Math.random().toString(36).substring(2, 9)}`;
  @Prop() showTermsModal: boolean = false;
  @Prop() termsTitle: string = "Chatbot Terms & Conditions";
  @Prop() termsDescription: string = "Please review our <a href='https://biel.ai/terms' target='_blank' rel='noopener'>Terms & Conditions</a> before proceeding.";
  @Prop() termsCheckboxText: string = "I have read and agree to the Terms & Conditions.";
  @Prop() sourcesText: string = 'Sources';
  @Prop({ mutable: true }) welcomeMessage: string;
  @Prop() suggestedQuestions: string = '';
  
  @State() isFullscreen: boolean = this.expandModal;
  @State() isLoading: boolean = false;
  @State() showPoweredBy: boolean = true;
  @State() parsedSuggestedQuestions: string[] = [];
  @State() messages: { text: string, sender: 'user' | 'ai', messageId?: string, feedback?: 0 | 1, sources?: { title: string, url: string }[], isPartial: boolean, partialId?: number }[] = [];
  @State() loadedChat: boolean = false;
  @State() isReceivingMessages: boolean = false;
  @State() termsAccepted: boolean = false;
  @State() termsDeclined: boolean = false;

  @Event() closeBot: EventEmitter<void>;
  @Event() addMessageText: EventEmitter<{ text: string, id: number, bielId: string }>;
  @Event() partialMessageComplete: EventEmitter<{ partialId: number, bielId: string, msg: { text: string, sender: 'user' | 'ai', messageId?: string, feedback?: 0 | 1, sources?: { title: string, url: string }[], isPartial: boolean, partialId?: number } }>;

  @Element() el: HTMLElement;

  @Listen('bielSearchButtonClick', { target: 'body' })
  handleSearchButtonClick() {
    this.handleClose(this.bielId);
  }

  @Listen('closeClicked', { target: 'body' })
  handleClose(event: CustomEvent<{ bielId: string }> | string) {
    let id: string;
    if (typeof event === 'string') {
      id = event;
    } else if (event.detail.bielId) {
      id = event.detail.bielId;
    }

    if (this.bielId === id) {
      const modal = this.el.shadowRoot.querySelector('.biel-bot-content') as HTMLElement;
      if (modal) {
        modal.style.animation = 'fadeOut 0.3s';
        setTimeout(() => {
          this.showModal = false;
          document.body.style.overflow = '';
          this.closeBot.emit();
        }, 299);
      }
    }
  }

  @Listen('fullscreenClicked', { target: 'body' })
  handleFullscreenClicked(event: CustomEvent<{ bielId: string }>) {
    if (this.bielId === event.detail.bielId) {
      this.isFullscreen = !this.isFullscreen;
      setTimeout(() => {
        document.body.style.overflow = this.isFullscreen ? 'hidden' : '';
      }, 300);
    }
  }

  @Listen('suggestedQuestionClick', { target: 'body' })
  handleQuestionSelected(event: CustomEvent<{ bielId: string, message: string }>) {
    if (!this.isLoading && !this.disableInput && !this.isReceivingMessages) {
      this.handleMessageSent(event);
    }
  }

  @Listen('feedbackSent', { target: 'body' })
  handleFeedback(event: CustomEvent<{ bielId: string, messageId: string, reaction: 0 | 1 }>) {
    if (!this.hideFeedback && this.bielId === event.detail.bielId) {
      const { messageId, reaction } = event.detail;
      this.bielService.sendFeedback(messageId, { reaction });
    }
  }

  @Listen('refreshClicked', { target: 'body' })
  handleRefreshCLicked(event: CustomEvent<{ bielId: string }>) {
    if (this.bielId === event.detail.bielId) {
      this.messages = [];
      this.conectWebsocket();
    }
  }

  @Listen('messageSent', { target: 'body' })
  handleMessageSent(event: CustomEvent<{ bielId: string, message: string }>) {
    if (event.detail.bielId === this.bielId) {
      this.messages = [...this.messages, { text: event.detail.message, sender: 'user', isPartial: false }];
      if (!this.disableInput && !this.isReceivingMessages) {
        this.webSocketService.sendMessage(event.detail.message);
      }
    }
  }

  @Listen('termsAccepted', { target: 'body' })
  handleTermsAccepted() {
    localStorage.setItem(this.TERMS_ACCEPTED_KEY, 'true');
    this.termsAccepted = true;
  }

  @Listen('termsDeclined', { target: 'body' })
  handleTermsDeclined(event: CustomEvent<boolean>) {
    this.termsDeclined = event.detail;
  }

  modalContent: HTMLElement;
  webSocketService: BielWebSocketService;
  bielService: BielService;

  constructor() {
    this.conectWebsocket();
  }

  conectWebsocket() {
    if (!this.disableInput) {
      this.webSocketService = new BielWebSocketService(this.project, this.errorMessageDefault, this.email);
      this.webSocketService.onMessage(this.handleIncomingMessage.bind(this));
      this.webSocketService.onLoadingStart(() => this.isLoading = true);
      this.webSocketService.onLoadingEnd(() => this.isLoading = false);
    }
  }

  handleIncomingMessage(message: any) {
    const lastMessage = this.messages[this.messages.length - 1];
    const partialId = this.messages.length;

    if (message.isPartial) {
      if (lastMessage?.sender !== 'ai') {
        this.messages = [...this.messages, { sender: message.from, text: message.message, isPartial: message.isPartial, partialId: partialId + 1 }];
      } else {
        this.addMessage(message.message, partialId);
        this.isReceivingMessages = true;
      }
    } else {
      const msg = { sender: message.from, text: message.message, messageId: message.messageId, sources: message.sources, isPartial: message.isPartial };
      this.isReceivingMessages = false;
      if (lastMessage.isPartial) {
        const bielId = this.bielId;
        this.partialMessageComplete.emit({ partialId, bielId, msg });
        this.messages[this.messages.length - 1] = msg;
      } else {
        this.messages = [...this.messages, msg];
      }
    }
  }

  addMessage(text: string, id: number) {
    setTimeout(() => {
      const bielId = this.bielId
      this.addMessageText.emit({ text, id, bielId });
    }, 100);
  }

  async componentWillLoad() {
    if (this.embeddedMode) {
      this.showModal = true;
      this.hideCloseButton = true;
      this.hideExpandButton = true;
      this.hideRefreshButton = true;
      this.modalPosition = 'default';
    }

    if (this.initialMessages.length > 0) {
      this.messages = [...this.initialMessages];
      this.loadedChat = true;
    }

    if (this.fetchData && !this.disableInput) {
      this.bielService = new BielService(
        this.project,
        this.errorMessage404,
        this.errorMessage403,
        this.errorMessageDefault
      );

      if (this.suggestedQuestions) {
        try {
          this.parsedSuggestedQuestions = JSON.parse(this.suggestedQuestions);
        } catch (e) {
          // If parsing fails, split by comma and trim whitespace
          this.parsedSuggestedQuestions = this.suggestedQuestions.split(',').map(q => q.trim());
        }
      }

      const result = await this.bielService.getProjectDetails();

      if (typeof result === 'string') {
        this.welcomeMessage = this.welcomeMessage || result;
      } else {
        this.showPoweredBy = !result.whitelabel;
        if (!this.parsedSuggestedQuestions?.length) {
          this.parsedSuggestedQuestions = result.suggestedQuestions;
        }
        if (!this.welcomeMessage) {
          this.welcomeMessage = result.initialMessage;
        }
      }
    }

    if (this.showTermsModal) {
      this.termsAccepted = localStorage.getItem(this.TERMS_ACCEPTED_KEY) === 'true';
    }
  }

  render() {
    return (
      <div class={`biel-bot-wrapper ${this.embeddedMode ? 'biel-bot-wrapper--embedded' : ''}`}>
        {this.showModal && this.showTermsModal && !this.termsAccepted && !this.termsDeclined && (
          <biel-terms-modal
            termsTitle={this.termsTitle}
            termsDescription={this.termsDescription}
            termsCheckboxText={this.termsCheckboxText}
          />
        )}
        {this.showModal && (this.termsAccepted || !this.showTermsModal) && (<div class="feedback-overlay"></div>)}
        {this.showModal && (this.termsAccepted || !this.showTermsModal) && (
          <div class={`biel-bot-content biel-bot-content--${this.modalPosition} ${this.showModal ? 'biel-bot-content--open' : ''} ${this.isFullscreen ? 'fullscreen' : ''}`} ref={el => (this.modalContent = el)}>
            <div class="biel-bot-header">
              <biel-header
                headerTitle={this.headerTitle}
                project={this.project}
                showPoweredBy={this.showPoweredBy}
                hideExpandButton={this.hideExpandButton}
                hideCloseButton={this.hideCloseButton}
                hideRefreshButton={this.hideRefreshButton}
                fullscreen={this.isFullscreen}
                bielId={this.bielId}>
              </biel-header>
            </div>
            <div class="biel-bot-body">
              <biel-chat
                messages={this.messages}
                welcomeMessage={this.welcomeMessage}
                hideFeedback={this.hideFeedback}
                loadedChat={this.loadedChat}
                suggestedQuestions={this.parsedSuggestedQuestions}
                suggestedQuestionsTitle={this.suggestedQuestionsTitle}
                isFullscreen={this.isFullscreen}
                isLoading={this.isLoading}
                embeddedMode={this.embeddedMode}
                modalPosition={this.modalPosition}
                bielId={this.bielId}
                sourcesText={this.sourcesText}>
              </biel-chat>
              <biel-input
                placeholderText={this.inputPlaceholderText}
                sendButtonText={this.sendButtonText}
                isDisabled={this.isLoading || this.disableInput || this.isReceivingMessages}
                bielId={this.bielId}>
              </biel-input>
            </div>
            <div class="biel-bot-footer" innerHTML={this.footerText}></div>
          </div>
        )}
      </div>
    );
  }
}